/**
 * 배포 직후 옛 청크 로드 실패 자동 복구 (FIKET-359)
 *
 * StaticSite 배포 시 해시 자산이 교체되고 옛 청크는 삭제된다. 배포 직전에 열려 있던 탭이
 * lazy 라우트로 이동하며 옛 청크를 dynamic import 하면 실패하고 사용자는 빈 화면을 본다.
 *
 * - Vite `vite:preloadError` 이벤트에서 1회 자동 새로고침 (installPreloadErrorReload)
 * - 라우터 errorElement(RouteErrorFallback)에서도 같은 패턴을 감지해 새로고침
 * - sessionStorage 타임스탬프로 쿨다운(30초) — 새로고침 후에도 실패하면 안내 화면으로 넘겨 무한 루프 방지
 *
 * web 에 동일 파일이 있다(apps/web/src/lib/chunkReload.ts). 고칠 때 양쪽을 같이.
 */

const RELOAD_STAMP_KEY = 'fw_chunk_reload_at'
export const RELOAD_COOLDOWN_MS = 30_000

const STALE_CHUNK_PATTERNS: readonly RegExp[] = [
  /Failed to fetch dynamically imported module/i, // Chrome
  /Importing a module script failed/i, // Safari
  /error loading dynamically imported module/i, // Firefox
  /Unable to preload CSS/i, // Vite preload 헬퍼(CSS 청크)
  /ChunkLoadError/i, // webpack 계열 — 방어적으로 포함
]

/** 에러가 "옛 청크를 못 불러온" 종류인지 판별 */
export function isStaleChunkError(error: unknown): boolean {
  const text =
    error instanceof Error ? `${error.name} ${error.message}` : typeof error === 'string' ? error : ''
  return STALE_CHUNK_PATTERNS.some((re) => re.test(text))
}

export interface ReloadDeps {
  now?: () => number
  /** null 이면 저장소 사용 불가로 취급(루프 가드 불가 → 새로고침 안 함) */
  storage?: Pick<Storage, 'getItem' | 'setItem'> | null
  reload?: () => void
}

function defaultStorage(): Pick<Storage, 'getItem' | 'setItem'> | null {
  try {
    return window.sessionStorage
  } catch {
    // 프라이버시 모드 등에서 접근 자체가 throw
    return null
  }
}

/**
 * 쿨다운 밖이면 새로고침하고 true. 쿨다운 안(직전 새로고침도 실패)이거나
 * sessionStorage 를 못 쓰면(루프 가드 불가) 새로고침하지 않고 false.
 */
export function reloadOnceForStaleChunk(deps: ReloadDeps = {}): boolean {
  const now = deps.now?.() ?? Date.now()
  const storage = deps.storage === undefined ? defaultStorage() : deps.storage
  if (!storage) return false
  try {
    const last = Number(storage.getItem(RELOAD_STAMP_KEY) ?? 0)
    if (now - last < RELOAD_COOLDOWN_MS) return false
    storage.setItem(RELOAD_STAMP_KEY, String(now))
  } catch {
    return false
  }
  ;(deps.reload ?? (() => window.location.reload()))()
  return true
}

/** main.tsx 에서 createRoot 전에 1회 호출 */
export function installPreloadErrorReload(): void {
  window.addEventListener('vite:preloadError', (event) => {
    // 새로고침을 예약했으면 에러 전파를 막는다(빈 화면·콘솔 노이즈 방지).
    // 쿨다운에 걸렸으면 그대로 throw 시켜 errorElement 가 안내 화면을 그리게 한다.
    if (reloadOnceForStaleChunk()) event.preventDefault()
  })
}
